import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { keystore } from "../lib/keystore";
import { useAuth } from "./AuthContext";
import { setIdentityDecryptor, clearIdentityCache } from "../lib/identity";
import * as C from "../lib/crypto";

const CryptoCtx = createContext(null);

export const CryptoProvider = ({ children }) => {
  const { user, refresh } = useAuth();
  const privRef = useRef(null);        // CryptoKey (ECDH private)
  const privJwkRef = useRef(null);     // JWK (for rewrap)
  const netB64Ref = useRef(null);      // network key raw b64
  const netKeyRef = useRef(null);      // imported AES CryptoKey
  const dmCache = useRef({});          // userId -> AES CryptoKey
  const beaconKeys = useRef({});       // beaconId -> { raw (b64), key (CryptoKey) }
  const refreshedRef = useRef(false);  // one-time /auth/me refresh once names are decryptable
  const [status, setStatus] = useState("loading"); // loading | needSetup | needUnlock | unlocked | none
  const [recoveryPhrase, setRecoveryPhrase] = useState(null);
  const [netReady, setNetReady] = useState(false); // true once the network key is imported & usable

  const importNet = async () => {
    if (netB64Ref.current) {
      netKeyRef.current = await C.importAesKeyB64(netB64Ref.current);
      // Let the API layer transparently decrypt encrypted names/headlines.
      setIdentityDecryptor(async (ct, iv) => {
        if (!netKeyRef.current) return null;
        try { return await C.decryptText(netKeyRef.current, ct, iv); } catch { return null; }
      });
      clearIdentityCache();
      setNetReady(true);
      migrateOwnIdentity().then(() => {
        // Re-pull /auth/me once so the current member's own name/headline (now
        // decryptable) are populated in the session, even if it first loaded blank.
        if (!refreshedRef.current) { refreshedRef.current = true; try { refresh && refresh(); } catch {} }
      });
    }
  };

  // One-time per member: move plaintext name/headline into enc_fields (ciphertext
  // under the shared key). Runs the first time a member unlocks after this ships.
  const migrateOwnIdentity = async () => {
    try {
      if (!user || !netKeyRef.current) return;
      const ef = user.enc_fields || {};
      const enc_fields = {};
      const encOne = async (v) => {
        const { ctB64, ivB64 } = await C.encryptText(netKeyRef.current, v || "");
        return { ct: ctB64, iv: ivB64 };
      };
      if (user.name && !(ef.name && ef.name.ct)) enc_fields.name = await encOne(user.name);
      if (user.headline && !(ef.headline && ef.headline.ct)) enc_fields.headline = await encOne(user.headline);
      if (Object.keys(enc_fields).length) await api.patch("/me", { enc_fields });
    } catch {}
  };

  // Admin bootstraps the shared network key the first time; everyone else gets it via envelope.
  const loadNetworkKey = useCallback(async () => {
    const { data: env } = await api.get("/crypto/envelope");
    if (env.exists) {
      netB64Ref.current = await C.unwrapKeyFromEnvelope(env.wrapped_network_key, env.iv, env.ephemeral_pub, privRef.current);
      await keystore.saveNetwork(user.id, netB64Ref.current);
      await importNet();
      return;
    }
    const cached = await keystore.loadNetwork(user.id);
    if (cached) { netB64Ref.current = cached; await importNet(); return; }
    if (user.role === "admin") {
      const { data: ne } = await api.get("/crypto/network-exists");
      if (!ne.exists) {
        netB64Ref.current = await C.generateNetworkKey();
        const me = await api.get("/crypto/me");
        const env2 = await C.wrapKeyToPublic(netB64Ref.current, me.data.public_key);
        await api.post("/crypto/envelope", { user_id: user.id, wrapped_network_key: env2.wrappedB64, iv: env2.ivB64, ephemeral_pub: env2.ephemeralPubB64 });
        await keystore.saveNetwork(user.id, netB64Ref.current);
        await importNet();
      }
    }
  }, [user]);

  const finishUnlock = async (jwk) => {
    privJwkRef.current = jwk;
    privRef.current = await C.importPrivateKey(jwk);
    await keystore.savePrivate(user.id, jwk);
    await loadNetworkKey();
    setStatus("unlocked");
  };

  // Create identity keys + recovery phrase, wrap, persist. Returns the recovery phrase to show once.
  const setupKeys = useCallback(async (password) => {
    const { privateKeyJwk, publicKeyB64 } = await C.generateIdentityKeyPair();
    const phrase = C.generateRecoveryPhrase();
    const pwSalt = C.newSaltB64(), recSalt = C.newSaltB64(), recSalt2 = C.newSaltB64();
    const pwKey = await C.deriveWrapKey(password, pwSalt);
    const recKey = await C.deriveWrapKey(phrase, recSalt);
    const wp = await C.wrapPrivateJwk(privateKeyJwk, pwKey);
    const wr = await C.wrapPrivateJwk(privateKeyJwk, recKey);
    const recovery_auth = await C.deriveRecoveryAuth(phrase, recSalt2);
    const payload = {
      public_key: publicKeyB64,
      wrapped_priv_pw: wp.ctB64, pw_salt: pwSalt, pw_iv: wp.ivB64,
      wrapped_priv_rec: wr.ctB64, rec_salt: recSalt, rec_iv: wr.ivB64,
      rec_salt2: recSalt2, recovery_auth,
    };
    await api.post("/crypto/keys", payload);
    await finishUnlock(privateKeyJwk);
    setRecoveryPhrase(phrase);
    return { phrase, payload };
  }, [user, loadNetworkKey]);

  // Replace the recovery phrase: new words made on this device, wrapping the already-unlocked key.
  const rotateRecoveryPhrase = useCallback(async (password, mfaCode) => {
    const jwk = privJwkRef.current;
    if (!jwk) throw new Error("Unlock your vault first.");
    const phrase = C.generateRecoveryPhrase();
    const recSalt = C.newSaltB64(), recSalt2 = C.newSaltB64();
    const wr = await C.wrapPrivateJwk(jwk, await C.deriveWrapKey(phrase, recSalt));
    const recovery_auth = await C.deriveRecoveryAuth(phrase, recSalt2);
    await api.post("/crypto/recovery/rotate", {
      password, mfa_code: mfaCode || "",
      wrapped_priv_rec: wr.ctB64, rec_salt: recSalt, rec_iv: wr.ivB64, rec_salt2: recSalt2, recovery_auth,
    });
    return phrase;
  }, []);

  // Phrase check-in: entirely on-device, nothing is written and the server learns nothing about the result.
  const checkRecoveryPhrase = useCallback(async (password, phrase) => {
    const { data } = await api.get("/crypto/me");
    const tryUnwrap = async (secret, ct, iv, salt) => {
      try { await C.unwrapPrivateJwk(ct, iv, await C.deriveWrapKey(secret, salt)); return true; } catch { return false; }
    };
    if (!(await tryUnwrap(password, data.wrapped_priv_pw, data.pw_iv, data.pw_salt))) return { passwordOk: false, phraseOk: false };
    return { passwordOk: true, phraseOk: await tryUnwrap(C.normalizePhrase(phrase), data.wrapped_priv_rec, data.rec_iv, data.rec_salt) };
  }, []);

  // Build the crypto blobs WITHOUT a server call (used during registration to bundle with /auth/register)
  const buildKeysForRegister = useCallback(async (password) => {
    const { privateKeyJwk, publicKeyB64 } = await C.generateIdentityKeyPair();
    const phrase = C.generateRecoveryPhrase();
    const pwSalt = C.newSaltB64(), recSalt = C.newSaltB64(), recSalt2 = C.newSaltB64();
    const pwKey = await C.deriveWrapKey(password, pwSalt);
    const recKey = await C.deriveWrapKey(phrase, recSalt);
    const wp = await C.wrapPrivateJwk(privateKeyJwk, pwKey);
    const wr = await C.wrapPrivateJwk(privateKeyJwk, recKey);
    const recovery_auth = await C.deriveRecoveryAuth(phrase, recSalt2);
    return {
      privateKeyJwk, phrase,
      crypto: {
        public_key: publicKeyB64,
        wrapped_priv_pw: wp.ctB64, pw_salt: pwSalt, pw_iv: wp.ivB64,
        wrapped_priv_rec: wr.ctB64, rec_salt: recSalt, rec_iv: wr.ivB64,
        rec_salt2: recSalt2, recovery_auth,
      },
    };
  }, []);

  // Forgot-password: prove identity with the recovery phrase, reset login password + re-wrap key.
  const resetWithRecovery = useCallback(async (email, phrase, newPassword) => {
    const { data } = await api.post("/auth/recovery-info", { email });
    if (!data.exists) throw new Error("No encrypted recovery is on file for that email.");
    const norm = C.normalizePhrase(phrase);
    const recKey = await C.deriveWrapKey(norm, data.rec_salt);
    const jwk = await C.unwrapPrivateJwk(data.wrapped_priv_rec, data.rec_iv, recKey); // throws if phrase wrong
    const recovery_auth = await C.deriveRecoveryAuth(norm, data.rec_salt2);
    const pwSalt = C.newSaltB64();
    const pwKey = await C.deriveWrapKey(newPassword, pwSalt);
    const wp = await C.wrapPrivateJwk(jwk, pwKey);
    await api.post("/auth/reset-with-recovery", {
      email, recovery_auth, new_password: newPassword,
      wrapped_priv_pw: wp.ctB64, pw_salt: pwSalt, pw_iv: wp.ivB64,
    });
    return { privateKeyJwk: jwk };
  }, []);

  const adoptRegistered = useCallback(async (privateKeyJwk, phrase) => {
    await finishUnlock(privateKeyJwk);
    setRecoveryPhrase(phrase);
  }, [user]);

  const unlockWithPassword = useCallback(async (password) => {
    const { data } = await api.get("/crypto/me");
    if (!data.has_keys) { setStatus("needSetup"); return false; }
    const key = await C.deriveWrapKey(password, data.pw_salt);
    const jwk = await C.unwrapPrivateJwk(data.wrapped_priv_pw, data.pw_iv, key);
    await finishUnlock(jwk);
    return true;
  }, [user]);

  const unlockWithRecovery = useCallback(async (phrase, newPassword) => {
    const { data } = await api.get("/crypto/me");
    const recKey = await C.deriveWrapKey(C.normalizePhrase(phrase), data.rec_salt);
    const jwk = await C.unwrapPrivateJwk(data.wrapped_priv_rec, data.rec_iv, recKey);
    // re-wrap with the new password
    const pwSalt = C.newSaltB64();
    const pwKey = await C.deriveWrapKey(newPassword, pwSalt);
    const wp = await C.wrapPrivateJwk(jwk, pwKey);
    await api.post("/crypto/rewrap", { wrapped_priv_pw: wp.ctB64, pw_salt: pwSalt, pw_iv: wp.ivB64 });
    await finishUnlock(jwk);
    return true;
  }, [user]);

  // Initialize only when identity/approval changes, not on every user-object update (e.g. dismissing a tip).
  const userKey = user === false ? false : user ? `${user.id}|${user.role}|${user.status}` : null;
  useEffect(() => {
    (async () => {
      if (user === false) {
        // logged out: wipe device keys
        privRef.current = null; netB64Ref.current = null; netKeyRef.current = null; dmCache.current = {}; beaconKeys.current = {};
        setNetReady(false);
        setIdentityDecryptor(null); clearIdentityCache();
        refreshedRef.current = false;
        await keystore.clear();
        setStatus("loading");
        return;
      }
      if (!user || (user.role !== "admin" && user.status !== "approved")) { setStatus("loading"); return; }
      try {
        const localPriv = await keystore.loadPrivate(user.id);
        if (localPriv) {
          privJwkRef.current = localPriv;
          privRef.current = await C.importPrivateKey(localPriv);
          try { await loadNetworkKey(); } catch {}
          setStatus("unlocked");
          return;
        }
        const { data } = await api.get("/crypto/me");
        setStatus(data.has_keys ? "needUnlock" : "needSetup");
      } catch {
        // storage blocked / corrupt local key / network hiccup, fall back to the
        // unlock gate rather than crashing or hanging on a blank screen.
        try {
          const { data } = await api.get("/crypto/me");
          setStatus(data.has_keys ? "needUnlock" : "needSetup");
        } catch {
          setStatus("needUnlock");
        }
      }
    })();
  }, [userKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const lock = useCallback(async () => {
    privRef.current = null; netB64Ref.current = null; netKeyRef.current = null; dmCache.current = {}; beaconKeys.current = {};
    setNetReady(false);
    setIdentityDecryptor(null); clearIdentityCache();
    await keystore.clear();
    setStatus("loading");
  }, []);

  // ---- DM ----
  const getDmKey = useCallback(async (otherUserId) => {
    if (dmCache.current[otherUserId]) return dmCache.current[otherUserId];
    const { data } = await api.get(`/crypto/public-key/${otherUserId}`);
    if (!data.public_key || !privRef.current) return null;
    const k = await C.deriveDmKey(privRef.current, data.public_key);
    dmCache.current[otherUserId] = k;
    return k;
  }, []);

  const encryptDm = useCallback(async (otherUserId, text) => {
    const k = await getDmKey(otherUserId);
    if (!k) return { text, enc: false };
    const { ctB64, ivB64 } = await C.encryptText(k, text);
    return { ct: ctB64, iv: ivB64, enc: true };
  }, [getDmKey]);

  const decryptDm = useCallback(async (otherUserId, msg) => {
    if (!msg.enc) return msg.text;
    const k = await getDmKey(otherUserId);
    if (!k) return null;
    try { return await C.decryptText(k, msg.ct, msg.iv); } catch { return null; }
  }, [getDmKey]);

  // ---- Network (feed/comments/profile) ----
  const hasNetwork = () => !!netKeyRef.current;

  const encryptNetText = useCallback(async (text) => {
    if (!netKeyRef.current) return { text, enc: false };
    const { ctB64, ivB64 } = await C.encryptText(netKeyRef.current, text || "");
    return { ct: ctB64, iv: ivB64, enc: true };
  }, []);

  const decryptNetText = useCallback(async (ct, iv) => {
    if (!netKeyRef.current) return null;
    try { return await C.decryptText(netKeyRef.current, ct, iv); } catch { return null; }
  }, []);

  const encryptNetBytes = useCallback(async (arrayBuffer) => {
    if (!netKeyRef.current) return null;
    return C.encryptBytes(netKeyRef.current, arrayBuffer);
  }, []);

  const decryptNetBytes = useCallback(async (arrayBuffer, iv) => {
    if (!netKeyRef.current) return null;
    return C.decryptBytes(netKeyRef.current, arrayBuffer, iv);
  }, []);

  const encryptDmBytes = useCallback(async (otherUserId, arrayBuffer) => {
    const k = await getDmKey(otherUserId);
    if (!k) return null;
    return C.encryptBytes(k, arrayBuffer);
  }, [getDmKey]);

  const decryptDmBytes = useCallback(async (otherUserId, arrayBuffer, iv) => {
    const k = await getDmKey(otherUserId);
    if (!k) return null;
    try { return await C.decryptBytes(k, arrayBuffer, iv); } catch { return null; }
  }, [getDmKey]);

  // Chunked (large video) media, decrypt a framed buffer straight into a Blob.
  const decryptNetChunkedToBlob = useCallback(async (arrayBuffer, mime) => {
    if (!netKeyRef.current) return null;
    try { return await C.decryptChunkedToBlob(netKeyRef.current, arrayBuffer, mime); } catch { return null; }
  }, []);

  const decryptDmChunkedToBlob = useCallback(async (otherUserId, arrayBuffer, mime) => {
    const k = await getDmKey(otherUserId);
    if (!k) return null;
    try { return await C.decryptChunkedToBlob(k, arrayBuffer, mime); } catch { return null; }
  }, [getDmKey]);

  // ---- Admin: hand a member the network key on approval ----
  const issueEnvelopeFor = useCallback(async (memberId) => {
    if (!netB64Ref.current) return;
    const { data } = await api.get(`/crypto/public-key/${memberId}`);
    if (!data.public_key) return;
    const env = await C.wrapKeyToPublic(netB64Ref.current, data.public_key);
    await api.post("/crypto/envelope", { user_id: memberId, wrapped_network_key: env.wrappedB64, iv: env.ivB64, ephemeral_pub: env.ephemeralPubB64 });
  }, []);

  // ---- Safety Review key (steward-only, flag-triggered) ----
  const safetyPrivRef = useRef(null); // imported ECDH private CryptoKey (steward)
  const safetyJwkB64Ref = useRef(null); // base64(JSON(jwk)) for re-wrapping to new stewards

  // Any member: encrypt a flagged item to the Safety Review public key with a
  // fresh per-item key (indirection keeps future two-person "break glass" easy).
  const encryptForSafety = useCallback(async (plaintext) => {
    const itemKeyB64 = await C.generateNetworkKey();
    const itemKey = await C.importAesKeyB64(itemKeyB64);
    const { ctB64, ivB64 } = await C.encryptText(itemKey, plaintext || "");
    // Prefer 2-of-N break-glass: split the item key across the stewards' identity
    // keys so no single steward can ever decrypt alone.
    try {
      const { data } = await api.get("/safety/steward-keys");
      const stewards = (data.stewards || []).filter((s) => s.public_key);
      if (stewards.length >= 2) {
        const secret = new Uint8Array(C.b642buf(itemKeyB64));
        const shares = C.shamirSplit(secret, stewards.length, 2);
        const wrapped = [];
        for (let i = 0; i < stewards.length; i++) {
          const w = await C.wrapKeyToPublic(C.buf2b64(shares[i]), stewards[i].public_key);
          wrapped.push({ steward_id: stewards[i].user_id, wrapped: w.wrappedB64, iv: w.ivB64, eph: w.ephemeralPubB64 });
        }
        return { ct: ctB64, iv: ivB64, shares: wrapped, k: 2, v: 2 };
      }
    } catch { /* fall back to single-key below */ }
    // Fallback (fewer than 2 stewards): single Safety Review key, any one steward.
    const { data } = await api.get("/safety/pubkey");
    if (!data.exists || !data.public_key) return null;
    const w = await C.wrapKeyToPublic(itemKeyB64, data.public_key);
    return { ct: ctB64, iv: ivB64, k_wrapped: w.wrappedB64, k_iv: w.ivB64, k_eph: w.ephemeralPubB64, v: 1 };
  }, []);

  // Steward: start a break-glass request for a flagged item (records who asked).
  const requestBreakGlass = useCallback(async (disturbanceId) => {
    const { data } = await api.post(`/safety/break-glass/${disturbanceId}/request`);
    return data;
  }, []);

  // Steward (not the requester): co-sign by unwrapping MY share and re-wrapping it to
  // the requester's identity key, so the two shares only ever meet on their device.
  const cosignBreakGlass = useCallback(async (req) => {
    if (!privRef.current || !req?.sr?.shares || !req.requester_public_key) return false;
    const mine = req.sr.shares.find((s) => s.steward_id === user.id);
    if (!mine) return false;
    const shareB64 = await C.unwrapKeyFromEnvelope(mine.wrapped, mine.iv, mine.eph, privRef.current);
    const w = await C.wrapKeyToPublic(shareB64, req.requester_public_key);
    await api.post(`/safety/break-glass/${req.id}/cosign`, { wrapped: w.wrappedB64, iv: w.ivB64, eph: w.ephemeralPubB64 });
    return true;
  }, [user]);

  // Requester: combine my own share + a co-signed share to decrypt the flagged item.
  const combineBreakGlass = useCallback(async (sr, cosigns) => {
    if (!privRef.current || !sr?.shares) return null;
    const shares = [];
    const mine = sr.shares.find((s) => s.steward_id === user.id);
    if (mine) { const b64 = await C.unwrapKeyFromEnvelope(mine.wrapped, mine.iv, mine.eph, privRef.current); shares.push(new Uint8Array(C.b642buf(b64))); }
    for (const cs of (cosigns || [])) {
      try { const b64 = await C.unwrapKeyFromEnvelope(cs.wrapped, cs.iv, cs.eph, privRef.current); shares.push(new Uint8Array(C.b642buf(b64))); } catch { /* skip */ }
      if (shares.length >= 2) break;
    }
    if (shares.length < 2) return null;
    const secret = C.shamirCombine(shares);
    const itemKey = await C.importAesKeyB64(C.buf2b64(secret));
    try { return await C.decryptText(itemKey, sr.ct, sr.iv); } catch { return null; }
  }, [user]);

  // Steward: unwrap the Safety Review private key from the envelope on their identity key.
  const unlockSafetyKey = useCallback(async () => {
    if (safetyPrivRef.current) return true;
    if (!privRef.current) return false;
    const { data } = await api.get("/safety/envelope");
    if (!data.exists) return false;
    const jwkB64 = await C.unwrapKeyFromEnvelope(data.wrapped_priv, data.iv, data.ephemeral_pub, privRef.current);
    safetyJwkB64Ref.current = jwkB64;
    const jwk = JSON.parse(new TextDecoder().decode(C.b642buf(jwkB64)));
    safetyPrivRef.current = await C.importPrivateKey(jwk);
    return true;
  }, []);

  // A provisioned steward shares the Safety Review key with any stewards who don't
  // yet hold it (so newly appointed stewards gain access without a re-init).
  const grantSafetyToStewards = useCallback(async () => {
    if (!safetyJwkB64Ref.current) return;
    try {
      const { data } = await api.get("/safety/stewards");
      for (const sid of (data.ids || [])) {
        const pk = await api.get(`/crypto/public-key/${sid}`);
        if (!pk.data.public_key) continue;
        const e = await C.wrapKeyToPublic(safetyJwkB64Ref.current, pk.data.public_key);
        await api.post("/safety/envelope", { user_id: sid, wrapped_priv: e.wrappedB64, iv: e.ivB64, ephemeral_pub: e.ephemeralPubB64 });
      }
    } catch {}
  }, []);

  // Steward: decrypt a flagged item's Safety Review payload.
  const decryptSafety = useCallback(async (sr) => {
    if (!sr || !safetyPrivRef.current) return null;
    try {
      const itemKeyB64 = await C.unwrapKeyFromEnvelope(sr.k_wrapped, sr.k_iv, sr.k_eph, safetyPrivRef.current);
      const itemKey = await C.importAesKeyB64(itemKeyB64);
      return await C.decryptText(itemKey, sr.ct, sr.iv);
    } catch { return null; }
  }, []);

  // Founder/steward bootstrap: generate the Safety Review keypair, wrap the private
  // key to self, and (as founder) issue it to any other stewards missing it.
  const initSafetyKey = useCallback(async () => {
    if (!privRef.current) return false;
    const me = await api.get("/crypto/me");
    const myPub = me.data.public_key;
    const { privateKeyJwk, publicKeyB64 } = await C.generateIdentityKeyPair();
    const jwkB64 = C.buf2b64(new TextEncoder().encode(JSON.stringify(privateKeyJwk)));
    const env = await C.wrapKeyToPublic(jwkB64, myPub);
    await api.post("/safety/init", { public_key: publicKeyB64, wrapped_priv: env.wrappedB64, iv: env.ivB64, ephemeral_pub: env.ephemeralPubB64 });
    safetyPrivRef.current = await C.importPrivateKey(privateKeyJwk);
    // Fan out to other stewards.
    try {
      const { data } = await api.get("/safety/stewards");
      for (const sid of (data.ids || [])) {
        const pk = await api.get(`/crypto/public-key/${sid}`);
        if (!pk.data.public_key) continue;
        const e = await C.wrapKeyToPublic(jwkB64, pk.data.public_key);
        await api.post("/safety/envelope", { user_id: sid, wrapped_priv: e.wrappedB64, iv: e.ivB64, ephemeral_pub: e.ephemeralPubB64 });
      }
    } catch {}
    return true;
  }, []);

  // ---- Per-Beacon content key (zero-knowledge subscriber teachings) ----
  // Owner: get (or mint) the content key for MY Beacon. Returns an imported AES key.
  const ensureMyBeaconKey = useCallback(async (beaconId) => {
    if (beaconKeys.current[beaconId]) return beaconKeys.current[beaconId].key;
    if (!privRef.current) return null;
    const me = await api.get("/crypto/me");
    const myPub = me.data.public_key;
    const { data } = await api.get("/beacon/mine/key");
    let rawB64;
    if (data.exists) {
      rawB64 = await C.unwrapKeyFromEnvelope(data.wrapped_key, data.iv, data.eph_pub, privRef.current);
    } else {
      rawB64 = await C.generateNetworkKey();
      const env = await C.wrapKeyToPublic(rawB64, myPub);
      await api.post("/beacon/mine/keys", { envelopes: [{ recipient_id: user.id, wrapped_key: env.wrappedB64, iv: env.ivB64, eph_pub: env.ephemeralPubB64 }] });
    }
    const key = await C.importAesKeyB64(rawB64);
    beaconKeys.current[beaconId] = { raw: rawB64, key };
    return key;
  }, [user]);

  // Owner: wrap the key to any entitled subscribers who don't hold it yet.
  const grantMyBeaconKeyToPending = useCallback(async (beaconId) => {
    const entry = beaconKeys.current[beaconId];
    if (!entry) return 0;
    const { data } = await api.get("/beacon/mine/pending-grants");
    const recips = data.recipients || [];
    const envelopes = [];
    for (const r of recips) {
      try {
        const env = await C.wrapKeyToPublic(entry.raw, r.public_key);
        envelopes.push({ recipient_id: r.recipient_id, wrapped_key: env.wrappedB64, iv: env.ivB64, eph_pub: env.ephemeralPubB64 });
      } catch { /* skip */ }
    }
    if (envelopes.length) await api.post("/beacon/mine/keys", { envelopes });
    return envelopes.length;
  }, []);

  // Viewer/subscriber: load my wrapped key for a Beacon. {key} | {key:null, pending}.
  const loadBeaconKey = useCallback(async (beaconId, handle) => {
    if (beaconKeys.current[beaconId]) return { key: beaconKeys.current[beaconId].key };
    if (!privRef.current) return { key: null };
    const { data } = await api.get(`/beacon/view/${handle}/key`);
    if (!data.exists) return { key: null, pending: !!data.pending };
    const rawB64 = await C.unwrapKeyFromEnvelope(data.wrapped_key, data.iv, data.eph_pub, privRef.current);
    const key = await C.importAesKeyB64(rawB64);
    beaconKeys.current[beaconId] = { raw: rawB64, key };
    return { key };
  }, []);

  return (
    <CryptoCtx.Provider value={{
      status, setStatus, recoveryPhrase, setRecoveryPhrase,
      setupKeys, buildKeysForRegister, adoptRegistered, unlockWithPassword, unlockWithRecovery, resetWithRecovery, rotateRecoveryPhrase, checkRecoveryPhrase, lock,
      encryptDm, decryptDm, getDmKey, encryptDmBytes, decryptDmBytes, decryptDmChunkedToBlob,
      hasNetwork, encryptNetText, decryptNetText, encryptNetBytes, decryptNetBytes, decryptNetChunkedToBlob,
      netReady,
      issueEnvelopeFor,
      ensureMyBeaconKey, grantMyBeaconKeyToPending, loadBeaconKey,
      encryptForSafety, unlockSafetyKey, decryptSafety, initSafetyKey, grantSafetyToStewards,
      requestBreakGlass, cosignBreakGlass, combineBreakGlass,
    }}>
      {children}
    </CryptoCtx.Provider>
  );
};

export const useCrypto = () => useContext(CryptoCtx);
